import { TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  AcceptedLegalDocuments,
  Mutation,
  MutationAcceptLegalDocumentsArgs,
  Query
} from 'graphql/generated/graphqlTypes';
import { ACCEPT_LEGAL_DOCUMENTS } from 'graphql/mutations';
import { GET_CURRENT_TENANT, GET_TENANT_LEGAL_DOCUMENTS } from 'graphql/queries';
import { useEffect } from 'react';
import { BaseHookProps } from '../../hooks.types';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_TENANT_LEGAL_DOCUMENTS_TYPED: TypedDocumentNode<Pick<Query, 'getTenantLegalDocuments'>> =
  GET_TENANT_LEGAL_DOCUMENTS;
const ACCEPT_LEGAL_DOCUMENTS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'acceptLegalDocuments'>,
  MutationAcceptLegalDocumentsArgs
> = ACCEPT_LEGAL_DOCUMENTS;

const useTenantLegalDocuments = ({ handleFetchError }: BaseHookProps) => {
  const { data, loading: tenantLegalDocumentsLoading, error } = useQuery(GET_TENANT_LEGAL_DOCUMENTS_TYPED);

  useEffect(() => {
    if (error) {
      handleFetchError('Error while fetching legal documents');
      console.error(error);
    }
  }, [error, handleFetchError]);

  const [
    acceptedLegalDocumentsMutation,
    { loading: acceptedLegalDocumentsLoading, error: acceptedLegalDocumentsError }
  ] = useMutation(ACCEPT_LEGAL_DOCUMENTS_TYPED, {
    refetchQueries: [{ query: GET_CURRENT_TENANT }],
    awaitRefetchQueries: true
  });

  useEffect(() => {
    if (acceptedLegalDocumentsError) {
      handleFetchError('Error while accepting legal documents');
      console.error(acceptedLegalDocumentsError);
    }
  }, [acceptedLegalDocumentsError, handleFetchError]);

  const acceptedLegalDocuments = async (acceptedLegalDocumentsVariables: AcceptedLegalDocuments) => {
    const result = await acceptedLegalDocumentsMutation({
      variables: {
        acceptedLegalDocuments: acceptedLegalDocumentsVariables
      }
    });
    return result;
  };

  return {
    tenantLegalDocuments: data?.getTenantLegalDocuments,
    tenantLegalDocumentsLoading,
    acceptedLegalDocuments,
    acceptedLegalDocumentsLoading
  };
};

export default useTenantLegalDocuments;
